// 轻量 Mock 适配器：拦截 axios adapter 并返回本地数据
// 开关策略：VITE_USE_MOCK 未设置时默认启用；设置为 'false' 明确关闭

const isMockEnabled = () => {
  const flag = import.meta.env.VITE_USE_MOCK
  return String(flag).toLowerCase() === 'true'
}

function ok(config, data) {
  return Promise.resolve({ data: { success: true, data }, status: 200, statusText: 'OK', headers: {}, config })
}

function okBlob(config, blob) {
  return Promise.resolve({ data: blob, status: 200, statusText: 'OK', headers: {}, config })
}

function notFound(config) {
  return Promise.resolve({ data: { success: false, message: 'Mock: Not Found' }, status: 404, statusText: 'Not Found', headers: {}, config })
}

function parseQuery(url) {
  const [path, qs] = url.split('?')
  const params = {}
  if (qs) {
    for (const pair of qs.split('&')) {
      const [k, v] = pair.split('=')
      params[decodeURIComponent(k)] = decodeURIComponent(v || '')
    }
  }
  return { path, params }
}

function parseRangeDays(range) {
  if (!range) return 7
  const v = String(range)
  if (v === 'custom') return 7
  if (v.endsWith('d')) {
    const n = Number(v.slice(0, -1))
    return Number.isFinite(n) && n > 0 ? n : 7
  }
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : 7
}

function makeList(total, page, pageSize, mapper) {
  const list = []
  const start = (page - 1) * pageSize + 1
  const end = Math.min(start + pageSize - 1, total)
  for (let i = start; i <= end; i++) list.push(mapper(i))
  return { list, total }
}

export async function handleMock(config) {
  const method = (config.method || 'get').toLowerCase()
  const url = (config.baseURL ? '' : '') + (config.url || '')
  const { path, params } = parseQuery(url)

  // 鉴权/管理员
  if (method === 'post' && path === '/admin/login') {
    return ok(config, {
      token: 'mock-token',
      admin: { id: 1, name: '超级管理员', role: 'super_admin', avatar: '' },
      permissions: [
        'dashboard:view', 'user:list', 'user:disable', 'user:delete', 'user:notify',
        'membership:list', 'membership:open', 'membership:extend', 'membership:upgrade', 'membership:notify',
        'statistics:view', 'logs:view', 'settings:manage'
      ],
    })
  }
  if (method === 'post' && path === '/admin/logout') return ok(config, {})
  if (method === 'get' && path === '/admin/verify') {
    return ok(config, {
      admin: { id: 1, name: '超级管理员', role: 'super_admin', avatar: '' },
      permissions: [
        'dashboard:view', 'user:list', 'user:disable', 'user:delete', 'user:notify',
        'membership:list', 'membership:open', 'membership:extend', 'membership:upgrade', 'membership:notify',
        'statistics:view', 'logs:view', 'settings:manage'
      ],
    })
  }
  if (method === 'get' && path === '/admin/info') {
    return ok(config, { id: 1, name: '超级管理员', role: 'super_admin', avatar: '' })
  }

  // Dashboard
  if (method === 'get' && path === '/admin/dashboard') {
    return ok(config, {
      totalUsers: 2345,
      totalUsersChange: 9,
      activity: 72,
      activityChange: 4,
      totalClasses: 560,
      totalClassesChange: 6,
      storage: 256,
      storageChange: 3,
      trendUsers: [12, 18, 16, 14, 22, 25, 28],
      trendActivity: [60, 62, 64, 66, 68, 70, 72],
      trendClasses: [6, 7, 8, 10, 9, 11, 13],
      trendStorage: [200, 205, 210, 215, 225, 240, 256],
      registerTrend: [15, 14, 16, 18, 20, 22, 24],
      regionDist: [
        { name: '华东', value: 42 },
        { name: '华南', value: 22 },
        { name: '华北', value: 18 },
        { name: '西南', value: 10 },
        { name: '其他', value: 8 },
      ],
    })
  }

  // 统计
  if (method === 'get' && path === '/admin/statistics/users') {
    // 用户增长与活跃趋势
    const days = parseRangeDays(params.range)
    const gen = (base) => Array.from({ length: days }, (_, i) => base + Math.round(Math.sin(i / 2) * 3) + i)
    return ok(config, {
      registerTrend: gen(12),
      activeTrend: gen(60).map(v => Math.min(100, v)),
    })
  }

  if (method === 'get' && path === '/admin/statistics/usage') {
    // 使用统计：班级等
    const days = parseRangeDays(params.range)
    const gen = (base) => Array.from({ length: days }, (_, i) => base + Math.round(Math.cos(i / 2) * 2) + Math.round(i * 0.8))
    const scope = params.scope || 'classes'
    if (scope === 'classes') {
      return ok(config, {
        classesTrend: gen(6),
        gradeDist: [
          { name: '一年级', value: 12 },
          { name: '二年级', value: 14 },
          { name: '三年级', value: 13 },
          { name: '四年级', value: 10 },
          { name: '五年级', value: 9 },
          { name: '六年级', value: 8 },
        ],
      })
    }
    return ok(config, { })
  }

  if (method === 'get' && path === '/admin/statistics/regions') {
    return ok(config, {
      regionDist: [
        { name: '华东', value: 40 },
        { name: '华南', value: 24 },
        { name: '华北', value: 18 },
        { name: '西南', value: 10 },
        { name: '其他', value: 8 },
      ],
      top10: [
        { name: '广东', value: 120 },
        { name: '浙江', value: 110 },
        { name: '江苏', value: 100 },
        { name: '山东', value: 90 },
        { name: '四川', value: 80 },
        { name: '河南', value: 70 },
        { name: '河北', value: 66 },
        { name: '湖北', value: 60 },
        { name: '湖南', value: 58 },
        { name: '安徽', value: 50 },
      ],
    })
  }

  // 用户
  if (method === 'get' && path === '/admin/users') {
    const page = Number(params.page || 1)
    const pageSize = Number(params.pageSize || 10)
    const total = 200
    const data = makeList(total, page, pageSize, (i) => ({
      id: i,
      name: `用户${i}`,
      school: '第一中学',
      mobile: '138****0000',
      email: 'user@example.com',
      status: i % 4 === 0 ? 'disabled' : 'active',
      membership: ['none','trial','term','year'][i % 4],
      createdAt: '2024-01-01 12:00:00'
    }))
    return ok(config, data)
  }
  if (method === 'put' && /\/admin\/users\/.+\/status$/.test(path)) return ok(config, {})
  if (method === 'delete' && /\/admin\/users\/.+$/.test(path)) return ok(config, {})

  // 会员
  if (method === 'get' && path === '/admin/memberships') {
    const page = Number(params.page || 1)
    const pageSize = Number(params.pageSize || 10)
    const total = 120
    const data = makeList(total, page, pageSize, (i) => ({
      id: i,
      name: `用户${i}`,
      school: '第一中学',
      type: ['trial','term','year'][i % 3],
      status: ['normal','expiring','expired'][i % 3],
      startAt: '2024-01-01 12:00:00',
      endAt: '2024-06-01 12:00:00',
      renewTimes: i % 5,
      amount: (i + 1) * 100,
    }))
    return ok(config, data)
  }
  if (method === 'get' && path === '/admin/memberships/export') {
    const csv = 'id,name,type,status\n1,用户1,term,normal\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    return okBlob({ ...config, responseType: 'blob' }, blob)
  }

  // 新增：会员开通/延期/升级
  if (method === 'post' && path === '/admin/memberships/open') return ok(config, { id: Date.now() })
  if (method === 'post' && path === '/admin/memberships/extend') return ok(config, { updated: true })
  if (method === 'post' && path === '/admin/memberships/upgrade') return ok(config, { updated: true })

  // 通知公告
  if (method === 'get' && path === '/admin/notifications') {
    const page = Number(params.page || 1)
    const pageSize = Number(params.pageSize || 10)
    const total = 36
    const data = makeList(total, page, pageSize, (i) => ({
      id: i,
      title: `公告标题 ${i}`,
      status: ['draft','published'][i % 2],
      createdAt: '2024-01-01 12:00:00',
      updatedAt: '2024-01-02 12:00:00',
      publishAt: i % 2 ? '2024-01-03 12:00:00' : '',
    }))
    return ok(config, data)
  }
  if (method === 'post' && path === '/admin/notifications') {
    return ok(config, { id: Date.now() })
  }
  if (method === 'put' && /\/admin\/notifications\/.+$/.test(path)) {
    return ok(config, {})
  }
  if (method === 'delete' && /\/admin\/notifications\/.+$/.test(path)) {
    return ok(config, {})
  }
  if (method === 'post' && /\/admin\/notifications\/.+\/publish$/.test(path)) {
    return ok(config, { status: 'published', publishAt: '2024-01-03 12:00:00' })
  }

  // 新增：用户批量通知
  if (method === 'post' && path === '/admin/users/notify') {
    return ok(config, { submitted: true })
  }

  // 日志
  if (method === 'get' && path === '/admin/logs/operations') {
    const page = Number(params.page || 1)
    const pageSize = Number(params.pageSize || 10)
    const total = 100
    const data = makeList(total, page, pageSize, (i) => ({
      time: '2024-01-01 12:00:00',
      operator: 'admin',
      type: ['auth','user','settings','other'][i % 4],
      action: 'update',
      target: `user:${i}`,
      ip: '127.0.0.1',
      ua: 'Chrome',
      result: 'success',
    }))
    return ok(config, data)
  }
  if (method === 'get' && path === '/admin/logs/operations/export') {
    const csv = 'time,operator,type,action\n2024-01-01 12:00:00,admin,auth,login\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    return okBlob({ ...config, responseType: 'blob' }, blob)
  }
  if (method === 'post' && path === '/admin/logs/clear') return ok(config, {})
  if (method === 'post' && path === '/admin/logs/operations') return ok(config, {})

  // 设置
  if (method === 'get' && path === '/admin/settings') {
    return ok(config, {
      siteName: '育见班级管理助手',
      locale: 'zh-CN',
      timezone: 'UTC+8',
      passwordPolicy: 'standard',
      loginLock: false,
      sessionTimeout: 120,
    })
  }
  if (method === 'put' && path === '/admin/settings') return ok(config, {})
  if (method === 'get' && path === '/admin/system/status') {
    return ok(config, { status: 'online' })
  }

  // 新增：设置子模块保存
  if (method === 'post' && path === '/admin/settings/email') return ok(config, { saved: true })
  if (method === 'post' && path === '/admin/settings/sms') return ok(config, { saved: true })
  if (method === 'post' && path === '/admin/settings/security') return ok(config, { saved: true })
  if (method === 'post' && path === '/admin/settings/ratelimit') return ok(config, { saved: true })

  // 默认：404
  return notFound(config)
}

export { isMockEnabled } 
